import { useEffect, useState } from "react";
import API from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { AirplaneTilt, Plus, Trash, Calendar as CalIcon, FirstAid, GraduationCap, Heart, Question } from "@phosphor-icons/react";
import DateTimePicker from "./DateTimePicker";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";

const REASON_META = {
  holiday:  { label: "Holiday",  icon: AirplaneTilt, colour: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  sick:     { label: "Sick",     icon: FirstAid,     colour: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300" },
  training: { label: "Training", icon: GraduationCap, colour: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
  personal: { label: "Personal", icon: Heart,        colour: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300" },
  other:    { label: "Other",    icon: Question,     colour: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300" },
};

export default function AvailabilityCard() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "holiday", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/users/me/unavailability");
      setEntries(data.unavailability || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.start_date) { toast.error("Pick a start date"); return; }
    setAdding(true);
    try {
      await API.post("/users/me/unavailability", form);
      toast.success("Unavailability added");
      setForm({ start_date: "", end_date: "", reason: "holiday", note: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this entry?")) return;
    try {
      await API.delete(`/users/me/unavailability/${id}`);
      load();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <div className="border border-border rounded-md p-4 bg-card" data-testid="availability-card">
      <div className="flex items-center gap-2 mb-3">
        <AirplaneTilt size={16} className="text-primary" />
        <h3 className="text-sm font-semibold">My availability</h3>
        <Badge variant="secondary" className="text-[10px]">Block-out days</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Mark holidays, sick days or training so admins are warned before assigning you work on those dates.</p>

      {/* Add form */}
      <div className="space-y-3 p-3 rounded-md border border-dashed border-border mb-4 bg-muted/20">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Start date</Label>
            <DateTimePicker date={form.start_date} onDateChange={(d) => setForm(f => ({ ...f, start_date: d }))} label="Pick start" data-testid="availability-start" />
          </div>
          <div>
            <Label>End date <span className="text-[10px] text-muted-foreground">(optional, defaults to start)</span></Label>
            <DateTimePicker date={form.end_date} onDateChange={(d) => setForm(f => ({ ...f, end_date: d }))} label="Pick end" data-testid="availability-end" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>Reason</Label>
            <Select value={form.reason} onValueChange={(v) => setForm(f => ({ ...f, reason: v }))}>
              <SelectTrigger data-testid="availability-reason"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(REASON_META).map(([k, m]) => (
                  <SelectItem key={k} value={k}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Note <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
            <Input value={form.note} onChange={(e) => setForm(f => ({ ...f, note: e.target.value }))} placeholder="e.g. annual leave" maxLength={120} data-testid="availability-note" />
          </div>
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={adding || !form.start_date} data-testid="availability-save">
            <Plus size={12} className="mr-1" /> {adding ? "Saving…" : "Add unavailability"}
          </Button>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <p className="text-xs text-muted-foreground">Loading…</p>
      ) : entries.length === 0 ? (
        <p className="text-xs text-muted-foreground italic text-center py-2">No upcoming unavailability — you're all systems go 🚀</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => {
            const meta = REASON_META[e.reason] || REASON_META.other;
            const Icon = meta.icon;
            const range = e.end_date && e.end_date !== e.start_date
              ? `${fmtDate(e.start_date)} → ${fmtDate(e.end_date)}`
              : e.start_date;
            return (
              <div key={e.id} className="flex items-center justify-between gap-2 p-2.5 rounded border border-border" data-testid={`availability-row-${e.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-1 rounded-full ${meta.colour}`}>
                    <Icon size={11} /> {meta.label}
                  </span>
                  <span className="text-xs font-mono">{range}</span>
                  {e.note && <span className="text-[11px] text-muted-foreground truncate">— {e.note}</span>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(e.id)} className="h-7 w-7 p-0 text-red-600" data-testid={`availability-delete-${e.id}`}>
                  <Trash size={12} />
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
